import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock, Ellipsis, Receipt, ShoppingBag, UtensilsCrossed } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { TarabeezaMark } from '@/components/TarabeezaMark';
import { useAppSelector } from '@/Redux/store';

const TABS = [
  { to: '/menu', key: 'guest.nav.menu', icon: UtensilsCrossed },
  { to: '/cart', key: 'guest.nav.cart', icon: ShoppingBag },
  { to: '/track', key: 'guest.nav.orders', icon: Clock },
  { to: '/bill', key: 'guest.nav.bill', icon: Receipt },
  { to: '/more', key: 'guest.nav.more', icon: Ellipsis },
];

/** Mobile-first: a bottom tab bar, one thumb, no chrome the guest doesn't need. */
export default function Layout() {
  const { t } = useTranslation();
  const session = useAppSelector((s) => s.guest.session);
  const cartCount = useAppSelector((s) =>
    s.guest.cart.reduce((sum, line) => sum + line.quantity, 0),
  );

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <header className="sticky top-0 z-10 flex h-14 items-center gap-2.5 border-b border-line bg-card px-4">
        {/* 28px: below the 48px threshold, so `tarabeezaMark` drops the tabletop
            ring by itself and the silhouette stays clean at this size. */}
        <TarabeezaMark size={28} decorative />
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-sm font-semibold">{session?.branchName}</div>
          {session?.tableNumber && (
            <div className="text-xs text-muted-foreground">
              {t('guest.table', { number: session.tableNumber })}
            </div>
          )}
        </div>
        <LanguageSwitcher />
      </header>

      <main className="flex-1 p-4 pb-24">
        <Outlet />
      </main>

      <nav className="fixed inset-x-0 bottom-0 z-10 grid grid-cols-5 border-t border-line bg-card pb-[env(safe-area-inset-bottom)]">
        {TABS.map(({ to, key, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              cn(
                'relative flex flex-col items-center gap-0.5 py-2.5 text-[11px]',
                isActive ? 'text-accent' : 'text-muted-foreground',
              )
            }
          >
            <Icon className="h-5 w-5" />
            <span className="truncate px-1">{t(key)}</span>
            {to === '/cart' && cartCount > 0 && (
              <span className="absolute top-1 end-[22%] grid h-4 min-w-4 place-items-center rounded-full bg-accent px-1 text-[10px] font-semibold text-on-accent">
                {cartCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
